import { CatTipoComision } from '@/components/filtros/filtros.component';
import SolucionService from '@/entities/solucion/solucion.service';
import AlertService from '@/shared/alert/alert.service';
import RevisionService from '@/entities/revision/revision.service';
import {
  TipoComision as TipoComisionInsaculacion,
  InsaculacionesApi,
  Evaluador,
  EvaluadorResumen,
  Investigador,
  Comision,
  TipoConvocatoria,
  EstadoInsaculacion,
} from '@conacyt/insaculacion-client';
import { EstadoSolucion } from '@/shared/model/enumerations/estado-solucion.model';
import { TipoComision } from '@/shared/model/enumerations/tipo-comision.model';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';
import { Filtro } from '@/shared/model/filtro.model';
import { MenuSolucion, Solucion } from '@/shared/model/snp/evaluacion.model';
import { Component, Inject, Vue } from 'vue-property-decorator';
import { TipoRevision } from '@/shared/model/enumerations/tipo-revision.model';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import { IPersonalBeca } from '@/shared/model/seguimiento/personal-beca.model';
import { ICatalogoArea } from '@/shared/model/snp/campo-conocimiento.model';
import CatalogosSnp from '@/shared/model/snp/catalogos-snp.factory';
import PermisosUsuarioSnp, { PantallaSnp } from '../permisos/permisos-snp.factory';

@Component
export default class EvaluacionSnpMixin extends Vue {
  @Inject('insaculacionesApi') protected readonly insaculacionesApi: () => InsaculacionesApi;
  @Inject('solucionService') protected readonly solucionService: () => SolucionService;
  @Inject('alertService') protected readonly alertService: () => AlertService;
  @Inject('revisionService') protected readonly revisionService: () => RevisionService;

  /**
   * Regresa las áreas visibles al usuario en sesión; Apoyos o administrativos de snp
   * @param personalBeca Usuario en sesión
   * @param tipoComision tipo de comisión
   * @param solucion solución
   * @returns areas visibles para el usuario
   */
  protected obtenerAreasParaAdministradoresOApoyos(
    personalBeca: IPersonalBeca,
    tipoComision: TipoComision,
    solucion: Solucion
  ): Promise<Array<ICatalogoArea>> {
    return new Promise<Array<ICatalogoArea>>(resolve => {
      if (PermisosUsuarioSnp.obtenerPermisos(PantallaSnp.EVALUACION_ROLES_ADMINISTRATIVOS).includes(personalBeca.rol)) {
        resolve(CatalogosSnp.obtenerAreasDeConocimiento().sort((a, b) => a.clave.localeCompare(b.clave)));
      } else if (RolAutoridad.ASISTENTE_COMISION == personalBeca.rol) {
        this.insaculacionesApi()
          .obtenerAreasApoyo(TipoConvocatoria.PosgradoNacional, this.obtenerClaveSolucionPorTipo(tipoComision, solucion), personalBeca.cvu)
          .then(res => resolve((res.data ?? []).sort((a, b) => a.clave.localeCompare(b.clave))))
          .catch(() => resolve([]));
      } else {
        resolve([]);
      }
    });
  }

  /**
   * Consulta las comisiones de evaluación visibles al usuario en sesión; Apoyos o administradores de snp
   * @param personalBeca usuario en sesión
   * @param tipoComision  tipo de comisión
   * @param solucion  solución
   * @param area área
   * @returns lista de comisiones visibles al apoyo
   */
  protected obtenerComisionesParaAdministradoresOApoyos(
    personalBeca: IPersonalBeca,
    tipoComision: TipoComision,
    solucion: Solucion,
    area: ICatalogoArea
  ): Promise<Array<Comision>> {
    return new Promise<Array<Comision>>(resolve => {
      if (PermisosUsuarioSnp.obtenerPermisos(PantallaSnp.EVALUACION_ROLES_ADMINISTRATIVOS).includes(personalBeca.rol)) {
        this.insaculacionesApi()
          .obtenerComisionesArea(TipoConvocatoria.PosgradoNacional, this.obtenerClaveSolucionPorTipo(tipoComision, solucion), area.clave)
          .then(res => resolve(res.data ?? []))
          .catch(() => resolve([]));
      } else if (RolAutoridad.ASISTENTE_COMISION == personalBeca.rol) {
        this.insaculacionesApi()
          .obtenerComisionesApoyo(
            TipoConvocatoria.PosgradoNacional,
            this.obtenerClaveSolucionPorTipo(tipoComision, solucion),
            area.clave,
            personalBeca.cvu
          )
          .then(res => resolve(res.data ?? []))
          .catch(() => resolve([]));
      } else {
        resolve([]);
      }
    });
  }

  protected obtenerEstadoDeInsaculacionOError(tipoComision: TipoComision, solucion: Solucion): Promise<EstadoInsaculacion> {
    return new Promise<EstadoInsaculacion>((resolve, reject) => {
      this.insaculacionesApi()
        .obtenerEstadoInsaculacion(this.obtenerClaveSolucionPorTipo(tipoComision, solucion), TipoConvocatoria.PosgradoNacional)
        .then(result => {
          resolve(result.data);
        })
        .catch(err => reject(new Error(err)));
    });
  }

  /**
   * Genera la lista de los tipos de comisión disponibles.
   * @returns lista de tipos de comisión
   */
  protected generarTipoComisiones(): CatTipoComision[] {
    const tipoComisiones: CatTipoComision[] = [];
    const tipoComisionKeys = Object.keys(TipoComision).filter(tipo => tipo != TipoComision.REVISORA);

    tipoComisionKeys.forEach(key => {
      tipoComisiones.push({ value: key, text: this.$t('apeironGwApp.TipoComision.' + key).toString(), disabled: false });
    });
    return tipoComisiones;
  }

  protected consultarMenuSoluciones(): Promise<Array<MenuSolucion>> {
    return this.consultarMenuSolucionesPorAnio(null);
  }

  protected consultarMenuSolucionesPorAnio(anio: number): Promise<Array<MenuSolucion>> {
    return new Promise<Array<MenuSolucion>>((resolve, reject) => {
      const filtro = new Filtro();
      filtro.estadoSolucion = EstadoSolucion.PUBLICADA;
      filtro.tiposSolucion = [TipoSolucion.POSGRADO_NACIONAL];
      filtro.anioNumero = anio;
      const paginationQuery = {
        page: 0,
        size: 1000,
        sort: ['titulo,desc'],
        filter: filtro,
      };
      this.solucionService()
        .retrieve(paginationQuery)
        .then(res => {
          const soluciones: Array<MenuSolucion> = [];

          (res?.data ?? []).forEach(solucion => {
            soluciones.push(new MenuSolucion(solucion.titulo, new Solucion(solucion.id, solucion.titulo)));
          });

          resolve(soluciones);
        })
        .catch(error => reject(new Error(error)));
    });
  }

  protected extraerArea(nombreComision: string): string {
    return nombreComision.match(/\d+/g)[0];
  }

  protected obtenerClaveSolucionPorTipo(tipoComision: TipoComision, solucion: Solucion): string {
    return tipoComision === TipoComision.DICTAMINADORA ? solucion.id : `${solucion.id}-R`;
  }

  protected obtenerClaveSolucionPorTipoInstaculacion(tipoComision: TipoComisionInsaculacion, solucion: Solucion): string {
    return tipoComision === TipoComisionInsaculacion.Dictaminadora ? solucion.id : `${solucion.id}-R`;
  }

  protected obtenerEvaluacionIdDeLaSolucion(
    solucionId: string,
    tipoRevision: TipoRevision,
    tipoComision: TipoComisionInsaculacion
  ): Promise<string> {
    return new Promise(resolve => {
      this.solucionService()
        .findEvaluacionDefinicion(solucionId) // Siempre va sin la -R
        .then(definicionEvaluacion => {
          let evaluacionId = '';
          for (const definicion of definicionEvaluacion.revisiones) {
            if (
              definicion.tipoRevision == tipoRevision &&
              (definicion.tipoComision == tipoComision || (tipoComision == 'REVISORA' && definicion.tipoComision == 'RECONSIDERACION'))
            ) {
              evaluacionId = definicion.id;
            }
          }
          resolve(evaluacionId);
        })
        .catch(() => resolve(''));
    });
  }

  /**
   * Inicia el objeto de evaluador vacío
   * @returns objeto inicializado
   */
  protected iniciarEvalaudor(): Evaluador {
    return {
      estado: undefined,
      idCampo: undefined,
      idDisciplina: undefined,
      idSubdisciplina: undefined,
      nombreComision: undefined,
      posicionIntegrante: undefined,
      rol: undefined,
      solicitudes: undefined,
      correo: undefined,
      cveNivelSni: undefined,
      cvu: undefined,
      fechaFinVigencia: undefined,
      fechaInicioVigencia: undefined,
      id: undefined,
      nombre: undefined,
      nombreInstitucion: undefined,
    };
  }

  protected iniciaEvaluadorResumenVacio(): EvaluadorResumen {
    return {
      estado: undefined,
      posicionIntegrante: undefined,
      rol: undefined,
      correo: undefined,
      cveNivelSni: undefined,
      cvu: undefined,
      fechaFinVigencia: undefined,
      fechaInicioVigencia: undefined,
      id: undefined,
      nombre: undefined,
      nombreInstitucion: undefined,
    };
  }

  protected iniciaInvestigadroVacio(): Investigador {
    return {
      idCampo: undefined,
      idDisciplina: undefined,
      idSubdisciplina: undefined,
      correo: undefined,
      cveNivelSni: undefined,
      cvu: undefined,
      fechaFinVigencia: undefined,
      fechaInicioVigencia: undefined,
      id: undefined,
      nombre: undefined,
      nombreInstitucion: undefined,
    };
  }

  protected iniciarComision(): Comision {
    return { clave: undefined, evaluadores: [], nombre: undefined, tipo: undefined };
  }
}
